import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Stack,
  Typography,
  Button,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
  Card,
  CardContent
} from "@mui/material";
import api from "../../helper/baseApi";
import { useApiClient } from "../../context/ApiClientContext";
import DateFilter from "./dateFIlter";

export default function Department({ onChange, startDate, endDate, onFilter }) {
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState("Select All");
  const { apiUrls: { mobileApiUrl } } = useApiClient();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const filterColor = theme.palette.primary.main;
  const hoverColor = theme.palette.primary.dark;

  const [showAll, setShowAll] = useState(false);
  const VISIBLE_LIMIT = 10;
  const visibleDepartments = showAll ? departments : departments.slice(0, VISIBLE_LIMIT);
  const hiddenCount = Math.max(0, departments.length - VISIBLE_LIMIT);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const apiUrl = `${mobileApiUrl}department/view`;
      const response = await api.get(apiUrl);

      const d = response.data?.data?.departmentList ?? response.data ?? [];

      const validDepartments = d.filter(
        (dept) =>
          dept &&
          dept.name &&
          typeof dept.name === "string" &&
          dept.name.trim() !== ""
      );

      setDepartments(validDepartments);

      if (validDepartments.length > 0) {
        const allIds = validDepartments.map((d) => d.departmentId);
        setSelected(allIds);
        if (onChange) onChange([...allIds]);
      }

    } catch (error) {
      console.error("Failed to fetch departments", error);
      setDepartments([]);
    }
  };

  const toggleChip = (deptId) => {
    const newSelection = selected.includes(deptId)
      ? selected.filter((id) => id !== deptId)
      : [...selected, deptId];

    setSelected(newSelection);
    if (onChange) onChange([...newSelection]);
  };

  const selectAll = () => {
    const allIds = departments.map((d) => d.departmentId);
    setSelected(allIds);
    setFilter("Select All");
    if (onChange) onChange([...allIds]);
  };

  const clearAll = () => {
    setSelected([]);
    setFilter("Clear All");
    if (onChange) onChange([]);
  };

  const handleSelectChange = (event) => {
    const newValue = Array.isArray(event.target.value) ? event.target.value : [];
    setSelected(newValue);
    if (onChange) onChange([...newValue]);
  };

  const buttonStyles = (isActive) => ({
    fontSize: "14px",
    borderRadius: 5,
    textTransform: "none",
    mt: 0.5,
    ml: 1,
    bgcolor: isActive ? filterColor : "transparent",
    color: isActive ? "#ffffff" : "inherit",
    "&:hover": {
      bgcolor: isActive ? hoverColor : "transparent",
    },
    borderColor: isActive ? filterColor : "inherit",
  });

  return (
    <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mt: 4, marginRight: 2 }}>
      <Card sx={{ flex: 1, borderRadius: 3, boxShadow: 2, backgroundColor: "background.main" }}>
        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
          {/* Filter Title and Date Filter */}
          <Typography variant="body" fontWeight="bold" gutterBottom>
            Filter
          </Typography>
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onFilter={onFilter}
          />

          {/* Department Header and Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mt: 3,
              mb:1,
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: "space-between"
            }}
          >
            <Typography
              variant="body"
              fontWeight="bold"
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            >
              Department
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={filter === "Select All" ? "contained" : "outlined"}
                size="small"
                onClick={selectAll}
                sx={buttonStyles(filter === "Select All")}
              >
                Select All
              </Button>
              <Button
                variant={filter === "Clear All" ? "contained" : "outlined"}
                size="small"
                onClick={clearAll}
                sx={buttonStyles(filter === "Clear All")}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          {/* Department Selection */}
          {isMobile ? (
            <Select
              multiple
              value={selected}
              onChange={handleSelectChange}
              displayEmpty
              fullWidth
              sx={{ minWidth: 160 }}
              renderValue={(selectedArr) => {
                if (selectedArr.length === 0) {
                  return <em>Select Departments</em>;
                }
                const names = departments
                  .filter((d) => selectedArr.includes(d.departmentId))
                  .map((d) => d.name);
                if (names.length <= 2) return names.join(", ");
                return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
              }}
            >
              <MenuItem disabled value="">
                <em>Select Departments</em>
              </MenuItem>
              {departments.map((dept) => (
                <MenuItem
                  key={dept.departmentId}
                  value={dept.departmentId}
                >
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <>
              {/* Desktop Chip View */}
              <Stack 
                direction="row" 
                flexWrap="wrap" 
                gap={1} 
                sx={{ mt: 1 }}
              >
                {visibleDepartments.map((dept) => {
                  const isSelected = selected.includes(dept.departmentId);
                  return (
                    <Tooltip key={dept.departmentId} title={dept.name} arrow>
                      <Chip
                        label={dept.name}
                        variant="filled"
                        onClick={() => toggleChip(dept.departmentId)}
                        sx={{
                          fontSize: 14,
                          flex: {
                            xs: "1 0 calc(100% - 8px)",
                            sm: "1 0 calc(50% - 8px)",
                            md: "1 0 calc(33.33% - 8px)",
                            lg: "1 0 calc(25% - 8px)",
                            xl: "1 0 calc(20% - 8px)",
                          },
                          maxWidth: {
                            xs: "100%",
                            sm: "calc(50% - 8px)",
                            md: "calc(33.33% - 8px)",
                            lg: "calc(25% - 8px)",
                            xl: "calc(20% - 8px)",
                          },
                          justifyContent: "center",
                          backgroundColor: isSelected ? "#EAEBDC" : "#f8f3f3",
                          color: isSelected ? "#000" : "#0000007a",
                          "&:hover": {
                            backgroundColor: isSelected ? "#EAEBDC" : "#dcdcdc",
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>

              {/* Show More/Less and Selection Count */}
              <Stack 
                direction="row" 
                alignItems="center" 
                spacing={1} 
                sx={{ mt: 2, justifyContent: "flex-end" }}
              >
                {hiddenCount > 0 && (
                  <Chip
                    label={showAll ? "Show less" : `Show ${hiddenCount} more`}
                    onClick={() => setShowAll(!showAll)}
                    variant="outlined"
                    sx={{
                      fontSize: 14,
                      height: 32,
                      cursor: "pointer",
                    }}
                  />
                )}
                <Typography 
                  variant="note" 
                  sx={{ 
                    alignSelf: "center", 
                    ml: 1, 
                    fontStyle: "italic" 
                  }}
                >
                  ({selected.length} selected)
                </Typography>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}